// TODO(brand): migrate to <Button>
import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { Loader2, Mail, Lock, User, AlertCircle, CheckCircle2, HelpCircle, Wrench, Link as LinkIcon, Zap } from 'lucide-react';

interface AuthProps {
  onDevBypass: () => void; // kept for App.tsx compatibility, no longer surfaced in UI
  hasJoinCode?: boolean;   // from ?join= URL param — team member bypass
  defaultMode?: 'login' | 'signup'; // from ?mode=signup URL param
}

type AuthMode = 'login' | 'signup';

const Auth: React.FC<AuthProps> = ({ onDevBypass: _onDevBypass, hasJoinCode, defaultMode }) => {
  const [mode, setMode] = useState<AuthMode>(hasJoinCode ? 'signup' : (defaultMode ?? 'login'));

  // Shared fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Manual Verification State
  const [showManualVerify, setShowManualVerify] = useState(false);
  const [manualLink, setManualLink] = useState('');

  // Resend Cooldown State
  const [resendCooldown, setResendCooldown] = useState(0);

  // Connection Check State
  const [connectionStatus, setConnectionStatus] = useState<'checking' | 'connected' | 'error'>('checking');

  useEffect(() => {
    checkSupabaseConnection();
    const hash = window.location.hash;
    if (hash && hash.includes('error=')) {
      const params = new URLSearchParams(hash.substring(1));
      const errorDesc = params.get('error_description');
      if (errorDesc) {
        setError(errorDesc.replace(/\+/g, ' '));
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }, []);

  useEffect(() => {
    let timer: any;
    if (resendCooldown > 0) {
      timer = setInterval(() => setResendCooldown(prev => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const checkSupabaseConnection = async () => {
    setConnectionStatus('checking');
    try {
      const { error } = await supabase.from('organizations').select('count', { count: 'exact', head: true });
      setConnectionStatus(error?.message?.includes('fetch') ? 'error' : 'connected');
    } catch {
      setConnectionStatus('error');
    }
  };

  const switchMode = (next: AuthMode) => {
    setMode(next);
    setError(null);
    setSuccessMessage(null);
  };

  // ── Login / Signup ────────────────────────────────────────────────────────

  const handleResendConfirmation = async () => {
    if (!email || resendCooldown > 0) return;
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: { emailRedirectTo: window.location.origin },
      });
      if (error) throw error;
      setSuccessMessage('Confirmation email resent!');
      setResendCooldown(60);
    } catch (err: any) {
      if (err.message.includes('rate limit') || err.message.includes('Too many requests')) {
        setError('Rate limit exceeded. Please wait a few minutes before trying again.');
        setResendCooldown(60);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManualVerify = async () => {
    if (!manualLink) return;
    setLoading(true);
    setError(null);
    try {
      let url: URL;
      try { url = new URL(manualLink); } catch { throw new Error('Invalid URL format. Please paste the full link starting with https://'); }
      const token = url.searchParams.get('token');
      const type = url.searchParams.get('type') as any;
      if (!token || !type) throw new Error('Could not find token or type in the link. Make sure you pasted the full verification URL.');
      const { data, error } = await supabase.auth.verifyOtp({ token, type, email });
      if (error) throw error;
      if (data.session) {
        setSuccessMessage('Successfully verified! Logging you in...');
      } else {
        setSuccessMessage('Email verified! Please log in with your password now.');
        switchMode('login');
        setManualLink('');
        setShowManualVerify(false);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      if (mode === 'login') {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          if (error.message.includes('Email not confirmed')) {
            throw new Error('Email not confirmed. Please check your inbox (and Spam folder) or use Manual Verification below.');
          }
          throw error;
        }
      } else {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { full_name: fullName },
            emailRedirectTo: window.location.origin,
          },
        });
        if (error) throw error;
        if (data.user && !data.session) {
          setSuccessMessage('Account created! Confirmation email sent.');
          switchMode('login');
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) {
      setError(error.message);
      setLoading(false);
    }
    // On success the browser redirects to Google — no further action needed here
  };

  const showResendButton = error && (
    error.toLowerCase().includes('expired') ||
    error.toLowerCase().includes('invalid') ||
    error.toLowerCase().includes('confirmed') ||
    error.toLowerCase().includes('rate limit')
  );

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-blue-600 p-8 text-center">
          <h1 className="text-4xl font-bold text-white mb-1">Sideout<span className="text-orange-500">IQ</span></h1>
          <p className="text-sm font-semibold tracking-widest text-white/80 uppercase">Command the Court</p>
        </div>

        <div className="p-8">
          {/* Tabs */}
          <div className="flex gap-4 mb-6 border-b border-slate-100 pb-2">
            <button
              onClick={() => switchMode('login')}
              className={`flex-1 pb-2 text-sm font-semibold transition-colors ${mode === 'login' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-400'}`}
            >
              Sign In
            </button>
            <button
              onClick={() => switchMode('signup')}
              className={`flex-1 pb-2 text-sm font-semibold transition-colors ${mode === 'signup' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-400'}`}
            >
              Request Beta Access
            </button>
          </div>

          {/* Google SSO */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 border border-slate-300 rounded-xl py-3 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 active:scale-[0.98] transition-all disabled:opacity-50"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"/>
              <path fill="#FBBC05" d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332Z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 6.293C4.672 4.166 6.656 3.58 9 3.58Z"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-2">
            <div className="flex-1 h-px bg-slate-100" />
            <span className="text-xs text-slate-400 font-medium">or</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          <form onSubmit={handleAuth} className="space-y-4">
              {/* Team join banner */}
              {mode === 'signup' && hasJoinCode && (
                <div className="p-3.5 bg-blue-50 border border-blue-100 rounded-xl flex gap-3 items-start">
                  <CheckCircle2 className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-blue-800 font-medium">You've been invited to join a team. Create an account to continue.</p>
                </div>
              )}

              {mode === 'signup' && !hasJoinCode && (
                <div className="p-4 bg-gradient-to-br from-blue-50 to-slate-50 border border-blue-100 rounded-xl flex gap-3 items-start shadow-sm">
                  <Zap className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-slate-700 font-medium leading-relaxed">
                    You're early! We are currently in closed beta. Request access to secure your spot in the next rollout.{' '}
                    <a href="https://www.sideoutiq.com/faq" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold hover:text-blue-700 hover:underline">
                      Learn more →
                    </a>
                  </p>
                </div>
              )}

              {mode === 'signup' && (
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Full Name</label>
                  <div className="relative">
                    <User className="w-5 h-5 absolute left-3 top-3 text-slate-400" />
                    <input type="text" required value={fullName} onChange={e => setFullName(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white text-slate-900 placeholder:text-slate-400"
                      placeholder="Coach Carter" />
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-500 uppercase">Email</label>
                <div className="relative">
                  <Mail className="w-5 h-5 absolute left-3 top-3 text-slate-400" />
                  <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white text-slate-900 placeholder:text-slate-400"
                    placeholder="coach@example.com" />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-500 uppercase">Password</label>
                <div className="relative">
                  <Lock className="w-5 h-5 absolute left-3 top-3 text-slate-400" />
                  <input type="password" required value={password} onChange={e => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white text-slate-900 placeholder:text-slate-400"
                    placeholder="••••••••" />
                </div>
              </div>

              {successMessage && (
                <div className="bg-green-50 text-green-700 p-4 rounded-lg text-sm border border-green-200 flex flex-col gap-2">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
                    <span className="font-bold">{successMessage}</span>
                  </div>
                  <div className="ml-8 text-xs text-green-800 space-y-1">
                    <p>1. Check your inbox for the confirmation link.</p>
                    <p>2. <strong>Check Spam / Junk folder</strong>.</p>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 text-red-600 p-4 rounded-lg text-sm border border-red-200 flex flex-col gap-2">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                    <span className="font-bold">{error}</span>
                  </div>
                  {showResendButton && (
                    <button type="button" onClick={handleResendConfirmation} disabled={loading || resendCooldown > 0}
                      className="ml-8 text-xs font-bold text-red-700 hover:text-red-800 hover:underline text-left">
                      {resendCooldown > 0 ? `Wait ${resendCooldown}s to resend` : 'Resend Confirmation Email'}
                    </button>
                  )}
                </div>
              )}

              {showManualVerify && (
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3 animate-fade-in-down mt-4">
                  <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                    <Wrench className="w-4 h-4" /> Manual Verification
                  </h3>
                  <p className="text-xs text-slate-500">Copy the <strong>entire link</strong> from the email and paste it here:</p>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <LinkIcon className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                      <input type="text" value={manualLink} onChange={e => setManualLink(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://...confirmation_token=..." />
                    </div>
                    <button type="button" onClick={handleManualVerify} disabled={!manualLink || loading}
                      className="bg-slate-800 text-white px-3 py-2 rounded-lg text-xs font-bold hover:bg-slate-700 disabled:opacity-50">
                      Verify
                    </button>
                  </div>
                </div>
              )}

              <button type="submit" disabled={loading}
                className="w-full bg-blue-600 text-white font-bold py-3.5 rounded-xl mt-6 hover:bg-blue-700 active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20">
                {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                {mode === 'login' ? 'Sign In' : 'Request Beta Access'}
              </button>

              <div className="mt-4 flex justify-between items-center">
                <button type="button" onClick={() => setShowManualVerify(!showManualVerify)}
                  className="text-xs text-slate-400 hover:text-slate-600 font-medium flex items-center gap-1">
                  <HelpCircle className="w-3 h-3" /> Trouble verifying?
                </button>
              </div>
          </form>

          <div className="mt-8 text-center pt-6 border-t border-slate-100">
            <div className="flex justify-center items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${connectionStatus === 'connected' ? 'bg-green-500' : connectionStatus === 'checking' ? 'bg-yellow-500' : 'bg-red-500'}`} />
              <span className="text-xs text-slate-400">
                {connectionStatus === 'connected' ? 'System Online' : connectionStatus === 'checking' ? 'Connecting…' : 'Offline'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
